/**
 * One metric inside a game card's parchment stat panel (APP_SPEC §13.3):
 * emblematic icon on the left, its label and value stacked beside it.
 *
 * Deliberately *not* the hero's `ProfileStatTile`. The hero has room for a
 * centred icon/label/value column; a game card has six of these inside a
 * ~150px panel, so the horizontal form is what keeps the panel compact and
 * makes it read like a character sheet rather than a KPI dashboard.
 *
 * The whole `[icon] + [label/value]` cluster is ONE content group, and the
 * group is what the cell centres — on both axes. It is an `inline-flex` that
 * shrink-wraps its contents (`w-fit`), never a block that fills the cell and
 * then left-aligns its children: those two produce identical CSS-level claims
 * of "centred" and completely different pictures, the second one being a
 * cluster pinned to the left wall of an over-wide cell with the balance of the
 * cell empty to its right. Inside the group the text stays left-aligned, so a
 * label that wraps still wraps against a straight edge.
 *
 * Label and value share one serif family and one ink colour — the label is
 * subordinate by size, not by being set in UI sans and washed out to brown.
 *
 * It carries no border, background, radius or shadow of its own — the panel
 * separates the cells with hairlines and whitespace — and the icon is
 * decorative, the label always carrying the meaning
 * (docs/UI_GUIDELINES.md §9).
 */
export interface GameStatProps {
  iconSrc: string;
  label: string;
  value: string;
  /**
   * How much width the cell has. `roomy` is a panel split into halves;
   * `dense` is a three-across row, where the same icon and label would push a
   * label like "Best word score" onto a second line and break the row's shared
   * baseline. The grid decides this from its own column count — no component
   * here knows which game it is rendering.
   */
  density?: 'roomy' | 'dense';
}

export function GameStat({ iconSrc, label, value, density = 'roomy' }: GameStatProps) {
  const dense = density === 'dense';
  const iconClass = dense
    ? 'h-9 w-9 tablet:h-[38px] tablet:w-[38px] wide:h-10 wide:w-10'
    : 'h-9 w-9 tablet:h-10 tablet:w-10 wide:h-11 wide:w-11';
  const labelClass = dense
    ? 'text-[0.85rem] wide:text-[0.9rem]'
    : 'text-[0.9rem] tablet:text-[0.95rem]';
  const valueClass = dense
    ? 'text-[1.45rem] wide:text-[1.55rem]'
    : 'text-[1.5rem] wide:text-[1.6rem]';

  return (
    <div
      className={`inline-flex w-fit min-w-0 max-w-full items-center ${dense ? 'gap-2 tablet:gap-2.5' : 'gap-2.5 tablet:gap-3'}`}
    >
      <img
        src={iconSrc}
        alt=""
        aria-hidden="true"
        className={`${iconClass} shrink-0 object-contain`}
      />
      <div className="min-w-0">
        <p className={`font-profile font-semibold leading-tight text-textPrimary ${labelClass}`}>
          {label}
        </p>
        <p
          className={`mt-0.5 font-profile font-bold leading-[1.1] text-textPrimary [font-variant-numeric:lining-nums_tabular-nums] ${valueClass}`}
        >
          {value}
        </p>
      </div>
    </div>
  );
}
